import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity } from '../common/entity';
import dayjs from 'dayjs';
import { toMap } from '../../util/map.util';
import { Unit, UnitOfMeasure, UnitSystem, UnitType } from '../uom';
import { UnitOfMeasureUtil } from '../uom';
import { DateTimeUtil } from '../../util/date.util';

export enum CustomFieldType {
  text = 'text',
  date = 'date',
  bool = 'bool',
  listOfValues = 'listOfValues',
  listOfTuples = 'listOfTuples',
  file = 'file',
  number = 'number',
  image = 'image',
}

export enum CustomFieldEntityType {
  order = 'order',
  productMasterData = 'productMasterData',
  productTransaction = 'productTransaction',
  containerTransaction = 'containerTransaction',
  lot = 'lot',
  bin = 'bin',
  container = 'container',
  product = 'product',
  asset = 'asset',
  assetTransaction = 'assetTransaction',
  companyRole = 'companyRole',
  contact = 'contact',
}

export enum CustomFieldEntitySubType {
  inbound = 'inbound',
  outbound = 'outbound',
  replenish = 'replenish',
  move = 'move',
  disposal = 'disposal',
}

export enum CustomFieldEntityParentType {
  order = 'order',

  ownership = 'ownership',
}

export enum CustomFieldEntityChildType {
  container = 'container',
}

export class CustomField extends CompanyEntity {
  type: CustomFieldType = CustomFieldType.text;
  entityType!: CustomFieldEntityType;
  entitySubtype?: CustomFieldEntitySubType;
  entityParentType?: CustomFieldEntityParentType;
  entityChildType?: CustomFieldEntityChildType;
  name!: string;
  mandatory: boolean = false;
  defaultValue?: string;
  index: number = 0;
  values: string[] = [];
  reference?: CustomFieldEntityType;
  manual?: boolean;
  displayMobile: boolean = false;

  generated: boolean = false;
  hidden: boolean = false;
  onCreate: boolean = true;

  parentId?: string;

  unitOfMeasure: UnitOfMeasure = UnitOfMeasure.default();

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, cloneDeep(obj));
  }

  withUnitType(unitType?: UnitType) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};

    this.unitOfMeasure.unitType = unitType;
    this.unitOfMeasure.unit = undefined;

    return this.withUnitSystem(UnitSystem.metric);
  }

  withUnitSystem(system?: UnitSystem) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};
    this.unitOfMeasure.system = system;
    return this.withUnit(UnitOfMeasureUtil.defaultUnit(this.unitOfMeasure));
  }

  withUnit(unit?: Unit) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};
    this.unitOfMeasure.unit = unit;
    return cloneDeep(this);
  }

  validEntitySubtypes(): CustomFieldEntitySubType[] {
    if (!this.entityType) return [];

    switch (this.entityType) {
      case CustomFieldEntityType.order:
        return [];
      case CustomFieldEntityType.productTransaction:
        return [CustomFieldEntitySubType.inbound, CustomFieldEntitySubType.outbound, CustomFieldEntitySubType.move];
      case CustomFieldEntityType.containerTransaction:
        return [CustomFieldEntitySubType.outbound];
      case CustomFieldEntityType.bin:
      case CustomFieldEntityType.productMasterData:
      case CustomFieldEntityType.companyRole:
      case CustomFieldEntityType.lot:
      case CustomFieldEntityType.container:
      case CustomFieldEntityType.product:
      case CustomFieldEntityType.asset:
      case CustomFieldEntityType.contact:
      case CustomFieldEntityType.assetTransaction:
        return [];
    }
  }

  validTypes(): CustomFieldType[] {
    if (!this.entityType) return [];

    switch (this.entityType) {
      case CustomFieldEntityType.productTransaction:
      case CustomFieldEntityType.containerTransaction:
        return [
          CustomFieldType.bool,
          CustomFieldType.date,
          CustomFieldType.text,
          CustomFieldType.file,
          CustomFieldType.number,
          CustomFieldType.listOfValues,
        ];
      case CustomFieldEntityType.productMasterData:
        return [CustomFieldType.bool, CustomFieldType.date, CustomFieldType.text, CustomFieldType.listOfValues];
      case CustomFieldEntityType.lot:
      case CustomFieldEntityType.container:
      case CustomFieldEntityType.bin:
      case CustomFieldEntityType.order:
      case CustomFieldEntityType.companyRole:
        return [
          CustomFieldType.bool,
          CustomFieldType.date,
          CustomFieldType.text,
          CustomFieldType.listOfValues,
          CustomFieldType.number,
        ];
      case CustomFieldEntityType.product:
        return [CustomFieldType.text, CustomFieldType.date];
      case CustomFieldEntityType.contact:
        return [CustomFieldType.text];
      case CustomFieldEntityType.assetTransaction:
        return [
          CustomFieldType.bool,
          CustomFieldType.image,
          CustomFieldType.date,
          CustomFieldType.text,
          CustomFieldType.listOfValues,
          CustomFieldType.listOfTuples,
        ];
      case CustomFieldEntityType.asset:
        return [
          CustomFieldType.bool,
          CustomFieldType.date,
          CustomFieldType.text,
          CustomFieldType.listOfValues,
          CustomFieldType.listOfTuples,
        ];
    }
  }

  validEntityParentTypes(): CustomFieldEntityParentType[] {
    if (!this.entityType) return [];

    switch (this.entityType) {
      case CustomFieldEntityType.order:
      case CustomFieldEntityType.productMasterData:
      case CustomFieldEntityType.bin:
      case CustomFieldEntityType.container:
      case CustomFieldEntityType.lot:
      case CustomFieldEntityType.product:
      case CustomFieldEntityType.asset:
      case CustomFieldEntityType.companyRole:
      case CustomFieldEntityType.contact:
        return [];
      case CustomFieldEntityType.assetTransaction:
        return [CustomFieldEntityParentType.ownership];
      case CustomFieldEntityType.productTransaction:
      case CustomFieldEntityType.containerTransaction:
        return [CustomFieldEntityParentType.order];
    }
  }

  validEntityChildTypes(): CustomFieldEntityChildType[] {
    if (!this.entityType) return [];

    switch (this.entityType) {
      case CustomFieldEntityType.order:
        switch (this.entitySubtype) {
          case CustomFieldEntitySubType.outbound:
            return [CustomFieldEntityChildType.container];
          default:
            return [];
        }
      case CustomFieldEntityType.productMasterData:
      case CustomFieldEntityType.companyRole:
      case CustomFieldEntityType.bin:
      case CustomFieldEntityType.container:
      case CustomFieldEntityType.lot:
      case CustomFieldEntityType.productTransaction:
      case CustomFieldEntityType.containerTransaction:
      case CustomFieldEntityType.product:
      case CustomFieldEntityType.asset:
      case CustomFieldEntityType.assetTransaction:
      case CustomFieldEntityType.contact:
        return [];
    }
  }

  validReference(): boolean {
    if (this.type === CustomFieldType.listOfValues) return true;
    if (this.type === CustomFieldType.text) return true;

    return false;
  }

  toValue(value?: string | Date): CustomFieldValue {
    if (value instanceof Date) {
      value = dayjs(value).toISOString();
    }

    return {
      id: this.id,
      mandatory: this.mandatory,
      name: this.name,
      type: this.type,
      value: value ?? '',
      index: 0,
      generated: false,
      hidden: false,
      onCreate: true,
      displayMobile: this.displayMobile,
    };
  }

  static toValue(field: CustomField, value: string): CustomFieldValue {
    return {
      id: field.id,
      name: field.name,
      value: value,
      mandatory: field.mandatory,
      type: field.type,
      entityType: field.entityType,
      generated: field.generated,
      hidden: field.hidden,
      index: field.index,
      onCreate: field.onCreate,
      displayMobile: field.displayMobile,
    };
  }

  static getDefaultValue(field: CustomField): string {
    if (!field.defaultValue) return '';

    if (field.type === CustomFieldType.date) {
      const dayOffset = parseFloat(field.defaultValue);
      if (isNaN(dayOffset)) return '';
      return dayjs().add(dayOffset, 'day').format('YYYY-MM-DD');
    }

    return field.defaultValue;
  }
}

export interface CustomFieldValue {
  id: string;
  name: string;
  value: string;
  mandatory: boolean;
  type: CustomFieldType;
  entityType?: CustomFieldEntityType;
  generated: boolean;
  hidden: boolean;
  index: number;
  onCreate: boolean;
  displayMobile?: boolean;
}

export type CustomFieldValues = Map<string, CustomFieldValue> | CustomFieldValue[];
export type EntityWithCustomFields = CompanyEntity & { customFields?: CustomFieldValues };

export class WithCustomFields {
  customFields: Map<string, CustomFieldValue>;

  constructor(customFields: CustomFieldValues) {
    if (Array.isArray(customFields)) this.customFields = toMap(customFields, 'id');
    else this.customFields = customFields;
  }

  static toCustomFieldsMap(obj: EntityWithCustomFields): Map<string, CustomFieldValue> {
    if (!obj.customFields) return new Map();

    let map = new Map();

    if (Array.isArray(obj.customFields)) {
      map = toMap(obj.customFields, 'id');
    } else if (obj.customFields instanceof Map) {
      map = new Map(obj.customFields);
    }

    for (const [key, value] of map) {
      if (value.type === CustomFieldType.date && value.value) {
        value.value = DateTimeUtil.toPureDate(value.value);
        map.set(key, value);
      }
    }

    return map;
  }

  customFieldValues(): CustomFieldValue[] {
    return [...this.customFields.values()];
  }

  withCustomField(value: CustomFieldValue | null, id?: string) {
    if (!value && !id) throw new Error('Either value or id must be provided');
    if (!value) return this.removeCustomField(id!);
    this.customFields = new Map(this.customFields).set(value.id, {
      id: value.id,
      name: value.name,
      value: value.value,
      mandatory: value.mandatory,
      type: value.type,
      entityType: value.entityType,
      generated: value.generated,
      hidden: value.hidden,
      index: value.index,
      onCreate: value.onCreate,
      displayMobile: value.displayMobile,
    });
    return cloneDeep(this);
  }

  removeCustomField(id: string) {
    this.customFields.delete(id);
    return cloneDeep(this);
  }

  static equal(field1: CustomFieldValue[] = [], field2: CustomFieldValue[] = []): boolean {
    if (field1.length !== field2.length) return false;

    for (const f1 of field1) {
      const f2 = field2.find(f => f.name === f1.name);
      if (!f2 || f1.value != f2.value) {
        return false;
      }
    }

    return true;
  }
}
